/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type ChatbotConversationCategoryType = {
  id: number;
  code: string;
  label: string;
  tabCode: string | null;
};

export type ChatbotConversationType = {
  id: number;
  uniqueId: string;
  structureUniqueId: string;
  categoryId: number;
  title: string | null;
  createdAt: string;
  updatedAt: string;
};

export type PendingDesignRuleFix = {
  issueName: string;
  issueMessage: string;
  failureContext: string;
  displayFailureContext: string;
  recommendations: {
    recommendation: string;
    aiInstructions: string;
    active: boolean;
    typeCode: string;
  }[];
};

export type PendingEvaluationIssue = {
  code: string;
  name: string;
  severity: string;
  message: string;
};

export type PendingEvaluation = {
  issues: PendingEvaluationIssue[];
};

export type ChatbotMessageType = {
  id: number;
  conversationId: number;
  role: string;
  content: string;
  orchestrateRequestId: string | null;
  refetchTargets: string | null;
  metadata: string | null;
  createdAt: string;
};

export interface ChatbotState {
  categories: ChatbotConversationCategoryType[];
  conversations: ChatbotConversationType[];
  activeConversationUniqueId: string | null;
  messages: ChatbotMessageType[];
  loadingCategories: boolean;
  loadingConversations: boolean;
  loadingMessages: boolean;
  sendingMessage: boolean;
  processingStatus: 'idle' | 'processing' | 'completed' | 'failed';
  pendingFix: PendingDesignRuleFix | null;
  pendingMultiFix: PendingDesignRuleFix[] | null;
  pendingEvaluation: PendingEvaluation | null;
}

export const defaultChatbotState: ChatbotState = {
  categories: [],
  conversations: [],
  activeConversationUniqueId: null,
  messages: [],
  loadingCategories: false,
  loadingConversations: false,
  loadingMessages: false,
  sendingMessage: false,
  processingStatus: 'idle',
  pendingFix: null,
  pendingMultiFix: null,
  pendingEvaluation: null
};

export const chatbotSlice = createSlice({
  name: 'chatbot',
  initialState: defaultChatbotState,
  reducers: {
    setCategories: (
      state,
      action: PayloadAction<ChatbotConversationCategoryType[]>
    ) => {
      state.categories = action.payload;
    },
    setConversations: (
      state,
      action: PayloadAction<ChatbotConversationType[]>
    ) => {
      state.conversations = action.payload;
    },
    setActiveConversationUniqueId: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.activeConversationUniqueId = action.payload;
    },
    addConversation: (
      state,
      action: PayloadAction<ChatbotConversationType>
    ) => {
      state.conversations = [action.payload, ...state.conversations];
    },
    setMessages: (state, action: PayloadAction<ChatbotMessageType[]>) => {
      state.messages = action.payload;
    },
    addMessage: (state, action: PayloadAction<ChatbotMessageType>) => {
      state.messages.push(action.payload);
    },
    setLoadingCategories: (state, action: PayloadAction<boolean>) => {
      state.loadingCategories = action.payload;
    },
    setLoadingConversations: (state, action: PayloadAction<boolean>) => {
      state.loadingConversations = action.payload;
    },
    setLoadingMessages: (state, action: PayloadAction<boolean>) => {
      state.loadingMessages = action.payload;
    },
    setSendingMessage: (state, action: PayloadAction<boolean>) => {
      state.sendingMessage = action.payload;
    },
    setProcessingStatus: (
      state,
      action: PayloadAction<'idle' | 'processing' | 'completed' | 'failed'>
    ) => {
      state.processingStatus = action.payload;
    },
    updateConversationTitle: (
      state,
      action: PayloadAction<{ uniqueId: string; title: string }>
    ) => {
      const conv = state.conversations.find(
        (c) => c.uniqueId === action.payload.uniqueId
      );
      if (conv) {
        conv.title = action.payload.title;
      }
    },
    setPendingFix: (
      state,
      action: PayloadAction<PendingDesignRuleFix | null>
    ) => {
      state.pendingFix = action.payload;
    },
    setPendingMultiFix: (
      state,
      action: PayloadAction<PendingDesignRuleFix[] | null>
    ) => {
      state.pendingMultiFix = action.payload;
    },
    setPendingEvaluation: (
      state,
      action: PayloadAction<PendingEvaluation | null>
    ) => {
      state.pendingEvaluation = action.payload;
    },
    clearChatbot: (state) => {
      state.conversations = [];
      state.activeConversationUniqueId = null;
      state.messages = [];
    }
  }
});

export const {
  setCategories,
  setConversations,
  setActiveConversationUniqueId,
  addConversation,
  setMessages,
  addMessage,
  setLoadingCategories,
  setLoadingConversations,
  setLoadingMessages,
  setSendingMessage,
  setProcessingStatus,
  updateConversationTitle,
  setPendingFix,
  setPendingMultiFix,
  setPendingEvaluation,
  clearChatbot
} = chatbotSlice.actions;

export default chatbotSlice.reducer;
